import type { AnswerUnit } from '@mathflat/domain/Problem'
import type { ScoringResult } from '@mathflat/domain/Scoring'
import type {
  StudentExamAutoScoringPayload,
  StudentExamProblemScoring,
} from '@mathflat/domain/StudentExam'
import type {
  StudentWorkbookAutoScoringPayload,
  StudentWorkbookScoring,
  StudentWorkbookScoringPayload,
  StudentWorkbookScoringProblem,
} from '@mathflat/domain/StudentWorkbook'
import type {
  StudentWorksheetScoring,
  WorksheetAutoScoringPayload,
  WorksheetScoringPayload,
} from '@mathflat/domain/StudentWorksheet'
import { makeAutoObservable } from 'mobx'

/** 시험 화면은 학습지 채점 화면을 재사용해 학습지·시험 문항을 한 목록에 든다 */
export type WorksheetScoring = StudentWorksheetScoring | StudentExamProblemScoring
export type WorkbookScoring = StudentWorkbookScoring<StudentWorkbookScoringProblem>
export type ScoringOf<T extends 'WORKSHEET' | 'WORKBOOK'> = T extends 'WORKSHEET'
  ? WorksheetScoring
  : WorkbookScoring

/** 다중 셀 로컬 입력 — 서버 계약(userAnswers)의 null 원소(미입력)를 UI 에서는 '' 로 다룬다. */
export type LocalUserAnswers = Exclude<NonNullable<WorksheetScoring['userAnswers']>[number], null>[]

// 서버에 보낼 때 문항을 가리키는 키. 교재 자동채점은 진도를 쿼리로 따로 받아 문제 키만 싣는다.
const toScoringKey = (scoring: WorksheetScoring | WorkbookScoring, auto: boolean) => {
  switch (scoring.kind) {
    case 'WORKSHEET':
      return { worksheetProblemId: scoring.id }
    case 'EXAM':
      return { examProblemId: scoring.id }
    case 'WORKBOOK':
      return auto
        ? { workbookProblemId: scoring.workbookProblemId }
        : {
            workbookProblemId: scoring.workbookProblemId,
            studentWorkbookProgressId: scoring.studentWorkbookProgressId,
          }
  }
}

export namespace RequestScoring {
  export class 자동채점<T extends 'WORKSHEET' | 'WORKBOOK' = 'WORKSHEET' | 'WORKBOOK'> {
    private _scoring: ScoringOf<T>

    localUnknown: boolean
    localUserAnswer: ScoringOf<T>['userAnswer']
    // 다중 셀 문제에서만 채워지고, 이때 localUserAnswer 는 무시된다.
    localUserAnswers: LocalUserAnswers | null

    constructor(params: {
      localUnknown: boolean
      localUserAnswer: ScoringOf<T>['userAnswer']
      localUserAnswers?: LocalUserAnswers | null
      scoring: ScoringOf<T>
    }) {
      makeAutoObservable(this)

      this._scoring = params.scoring
      this.localUnknown = params.localUnknown
      this.localUserAnswer = params.localUserAnswer
      this.localUserAnswers = params.localUserAnswers ?? null
    }

    get isNotLocallyScored() {
      if (this.localUnknown) {
        return false
      }
      if (this.localUserAnswers != null) {
        // 다중 셀은 모든 셀을 채워야 채점 대상 — 하나라도 비어 있으면 미입력으로 본다.
        // 빈 배열(모름 체크로 초기화된 뒤 해제된 상태)도 미입력이다.
        return this.localUserAnswers.length === 0 || this.localUserAnswers.some((cell) => !cell)
      }
      return !this.localUserAnswer || this.localUserAnswer === ''
    }

    static toRequestParams<
      T extends 'WORKSHEET' | 'WORKBOOK',
      U extends T extends 'WORKSHEET' ? '일반' | '시험지' : never = T extends 'WORKSHEET'
        ? '일반' | '시험지'
        : never,
    >(params: 자동채점<T>): AutoScoringRequest<T, U> {
      // 주관식 답안의 경우 값보정을 해줘야 한다.
      const convertedUserAnswer = 자동채점.toRequestLocalUserAnswer(params)

      // 서버 규약: 다중 셀은 userAnswers(미입력 빈칸은 '' 대신 null 원소 — 오답 처리),
      // 그 외는 userAnswer. 모름이면 답과 상호배타라 userAnswers 를 보내지 않는다.
      // 빈 배열(모름 체크로 초기화된 뒤 해제된 상태)은 셀 정보가 없는 미입력이다 —
      // userAnswers: [] 로 보내면 서버 에러가 나므로 null 로 보낸다.
      const isMultiCell = params.localUserAnswers != null && params.localUserAnswers.length > 0
      // 모름은 서버가 답 없는 항목을 조회에 내려주지 않아 unknown 플래그만으로는 복원이 불가능하다
      // — 다중 셀이어도 userAnswer 에 'UNKNOWN' 마커를 실어 보낸다.
      const defaultParams = {
        userAnswer: params.localUnknown
          ? convertedUserAnswer
          : isMultiCell
            ? null
            : convertedUserAnswer,
        userAnswers:
          isMultiCell && !params.localUnknown
            ? (params.localUserAnswers ?? []).map((cell) => cell || null)
            : null,
        unknown: params.localUnknown,
      }

      return {
        ...defaultParams,
        ...toScoringKey(params._scoring, true),
      } as AutoScoringRequest<T, U>
    }

    private static toRequestLocalUserAnswer(params: 자동채점) {
      if (!params.localUserAnswer) return null

      // 자동채점은 시그니처 교재 / 시험지 / 자동채점 학습지 / CAS 에서 사용. 모든 자동채점은 KaTeX 으로 변환되어야 한다.
      return buildKaTexUserAnswer({
        rawAnswer: params.localUserAnswer,
        answerUnits: params._scoring.answerUnits,
      })
    }
  }

  export class 일반채점<T extends 'WORKSHEET' | 'WORKBOOK' = 'WORKSHEET' | 'WORKBOOK'> {
    localResult: ScoringResult
    private _scoring: ScoringOf<T>

    constructor(params: { localResult: ScoringResult; scoring: ScoringOf<T> }) {
      makeAutoObservable(this)

      this.localResult = params.localResult
      this._scoring = params.scoring
    }

    // 제출하지 않음
    get isNotLocallyScored() {
      return this.localResult === 'NONE'
    }

    static toRequestParams<
      T extends 'WORKSHEET' | 'WORKBOOK',
      U extends T extends 'WORKSHEET' ? '일반' | '시험지' : never = T extends 'WORKSHEET'
        ? '일반' | '시험지'
        : never,
    >(params: 일반채점<T>): ScoringRequest<T, U> {
      const defaultParams = {
        result: params.localResult,
      }
      return { ...defaultParams, ...toScoringKey(params._scoring, false) } as ScoringRequest<T, U>
    }
  }
}

const FRAC_REGEX = /\\frac{(-?\w+)}{(-?\w+)}/g

// 사용자 입력값을 mathflatType / KaTeX으로 변경하는 로직
// 현재는 분수가 아니면 두 타입의 모양이 같기 때문에 분수일 때만 format이 일어남
const _formatUserAnswer = ({ userAnswer }: { userAnswer: string }) => {
  const formattedUserAnswer = userAnswer.replace(FRAC_REGEX, (match, numerator, denominator) => {
    // 분자 또는 분모 중 하나만 음수일 경우, -를 앞으로 옮김
    if (
      (numerator.startsWith('-') && !denominator.startsWith('-')) ||
      (!numerator.startsWith('-') && denominator.startsWith('-'))
    ) {
      const absoluteNumerator = Math.abs(numerator)
      const absoluteDenominator = Math.abs(denominator)

      return `-\\frac{${absoluteNumerator}}{${absoluteDenominator}}`
    }

    return `\\frac{${numerator}}{${denominator}}`
  })

  return formattedUserAnswer
}

export const getRefinedKaTexUnit = (unit: string) => {
  // square brackets 제거하고 space character를 "\; " 형식으로 치환
  const result = unit.replace(/[[\]]/g, '').replace(/\\; /g, ' ').replace(/ /g, '\\; ')

  return result || ''
}

// 자동채점 submit payload 의 userAnswer 빌더. 사용자 입력값을 KaTeX 으로 정규화하고 단위(prefix/postfix) 를 부착한다.
// 서버는 단위를 포함해 정답을 비교하므로, 단위가 누락되면 항상 오답으로 채점된다.
export const buildKaTexUserAnswer = ({
  rawAnswer,
  answerUnits,
}: {
  rawAnswer: string
  answerUnits: readonly AnswerUnit[] | null
}) => {
  if (!rawAnswer) return ''
  const prefixRaw = answerUnits?.find((item) => item.index === 0)?.unit
  const prefix = prefixRaw ? getRefinedKaTexUnit(prefixRaw) : ''

  const postfixRaw = answerUnits?.find((item) => item.index !== 0)?.unit
  const postfix = postfixRaw ? getRefinedKaTexUnit(postfixRaw) : ''

  const formattedUserAnswer = _formatUserAnswer({ userAnswer: rawAnswer })

  return `${prefix}${formattedUserAnswer}${postfix}`
}

export type AutoScoringRequest<
  T extends 'WORKSHEET' | 'WORKBOOK',
  U extends T extends 'WORKSHEET' ? '일반' | '시험지' : never = T extends 'WORKSHEET'
    ? '일반' | '시험지'
    : never,
> = T extends 'WORKBOOK'
  ? StudentWorkbookAutoScoringPayload[number]
  : U extends '일반'
    ? WorksheetAutoScoringPayload[number]
    : StudentExamAutoScoringPayload[number]

type ScoringRequest<
  T extends 'WORKSHEET' | 'WORKBOOK',
  U extends T extends 'WORKSHEET' ? '일반' | '시험지' : never = T extends 'WORKSHEET'
    ? '일반' | '시험지'
    : never,
> = T extends 'WORKBOOK'
  ? StudentWorkbookScoringPayload[number]
  : U extends '일반'
    ? WorksheetScoringPayload[number]
    : never // 시험지는 일반채점 없음
